import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { orgCatalogApi } from '@/lib/api';
import {
  GlobeAltIcon,
  SparklesIcon,
  StarIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';

interface Category {
  categoryId: string;
  name: string;
  slug: string;
  iconName?: string;
}

interface ListingTier {
  tier: 'basic' | 'featured' | 'spotlight';
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  maxCategories: number;
  badges: string[];
}

interface Listing {
  listingId: string;
  status: 'draft' | 'pending' | 'active' | 'suspended';
  tier: 'basic' | 'featured' | 'spotlight';
  headline: string;
  description: string;
  categories: string[];
  commissionHighlight: string;
  cookieDuration: number;
  benefits: string[];
  applicationMode: 'auto_approve' | 'manual_review';
  applicationRequirements?: string;
  minAudienceSize?: number;
  websiteUrl?: string;
  stats: {
    views: number;
    applications: number;
    approvals: number;
    activeAffiliates: number;
  };
  createdAt: string;
  tierExpiresAt?: string;
}

interface Application {
  applicationId: string;
  applicantName: string;
  applicantEmail: string;
  audienceDescription: string;
  websiteUrl?: string;
  monthlyVisitors?: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export default function OrgCatalog() {
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState<Listing | null>(null);
  const [tiers, setTiers] = useState<ListingTier[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [appStats, setAppStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState<'listing' | 'applications' | 'upgrade'>('listing');
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    headline: '',
    description: '',
    categories: [] as string[],
    commissionHighlight: '',
    cookieDuration: 30,
    benefits: ['', '', '', '', ''],
    applicationMode: 'manual_review' as 'auto_approve' | 'manual_review',
    applicationRequirements: '',
    minAudienceSize: 0,
    websiteUrl: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [listingRes, appsRes] = await Promise.all([
        orgCatalogApi.getListing(),
        orgCatalogApi.listApplications(),
      ]);

      if (listingRes.data.success) {
        setTiers(listingRes.data.data.tiers || []);
        setCategories(listingRes.data.data.categories || []);

        if (listingRes.data.data.listing) {
          const l = listingRes.data.data.listing;
          setListing(l);
          setFormData({
            headline: l.headline || '',
            description: l.description || '',
            categories: l.categories || [],
            commissionHighlight: l.commissionHighlight || '',
            cookieDuration: l.cookieDuration || 30,
            benefits: [...(l.benefits || []), '', '', '', '', ''].slice(0, 5),
            applicationMode: l.applicationMode || 'manual_review',
            applicationRequirements: l.applicationRequirements || '',
            minAudienceSize: l.minAudienceSize || 0,
            websiteUrl: l.websiteUrl || '',
          });
        }
      }

      if (appsRes.data.success) {
        setApplications(appsRes.data.data.applications || []);
        setAppStats(appsRes.data.data.stats || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('Failed to load catalog data:', error);
      toast.error('Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const benefits = formData.benefits.filter(b => b.trim() !== '');
      if (benefits.length === 0) {
        toast.error('Please add at least one benefit');
        setIsSaving(false);
        return;
      }

      const response = await orgCatalogApi.upsertListing({
        ...formData,
        benefits,
        minAudienceSize: formData.minAudienceSize || undefined,
      });

      if (response.data.success) {
        setListing(response.data.data.listing);
        toast.success(listing ? 'Listing updated!' : 'Listing created!');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to save listing');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteListing = async () => {
    if (!confirm('Are you sure you want to remove your listing from the catalog?')) return;

    try {
      const response = await orgCatalogApi.deleteListing();
      if (response.data.success) {
        setListing(null);
        setFormData({
          headline: '',
          description: '',
          categories: [],
          commissionHighlight: '',
          cookieDuration: 30,
          benefits: ['', '', '', '', ''],
          applicationMode: 'manual_review',
          applicationRequirements: '',
          minAudienceSize: 0,
          websiteUrl: '',
        });
        toast.success('Listing removed from catalog');
      }
    } catch {
      toast.error('Failed to remove listing');
    }
  };

  const handleReviewApplication = async (applicationId: string, action: 'approve' | 'reject') => {
    const promptResult = action === 'reject'
      ? prompt('Please provide a reason for rejection:')
      : null;

    if (action === 'reject' && !promptResult) return;

    try {
      const response = await orgCatalogApi.reviewApplication(applicationId, {
        action,
        rejectionReason: promptResult || undefined,
      });

      if (response.data.success) {
        toast.success(`Application ${action}d`);
        loadData();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || `Failed to ${action} application`);
    }
  };

  const toggleCategory = (slug: string) => {
    const currentTier = listing?.tier || 'basic';
    const tierConfig = tiers.find(t => t.tier === currentTier);
    const maxCategories = tierConfig?.maxCategories || 2;

    if (formData.categories.includes(slug)) {
      setFormData({
        ...formData,
        categories: formData.categories.filter(c => c !== slug),
      });
    } else if (formData.categories.length < maxCategories) {
      setFormData({
        ...formData,
        categories: [...formData.categories, slug],
      });
    } else {
      toast.error(`Your ${tierConfig?.name || 'Basic'} tier allows up to ${maxCategories} categories`);
    }
  };

  const updateBenefit = (index: number, value: string) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-white">Affiliate Catalog</h2>
          <p className="text-slate-400 mt-1">
            Get discovered by affiliates looking for programs to promote
          </p>
        </div>
        {listing && (
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              listing.status === 'active' ? 'bg-accent-900/50 text-accent-400' :
              listing.status === 'pending' ? 'bg-yellow-900/50 text-yellow-400' :
              'bg-slate-700 text-slate-400'
            }`}>
              {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              listing.tier === 'spotlight' ? 'bg-purple-900/50 text-purple-400' :
              listing.tier === 'featured' ? 'bg-primary-900/50 text-primary-400' :
              'bg-slate-700 text-slate-400'
            }`}>
              {listing.tier.charAt(0).toUpperCase() + listing.tier.slice(1)}
            </span>
          </div>
        )}
      </div>

      {/* Stats */}
      {listing && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-900/50 rounded-lg">
                <EyeIcon className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{listing.stats.views}</p>
                <p className="text-sm text-slate-400">Views</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent-900/50 rounded-lg">
                <UserGroupIcon className="w-5 h-5 text-accent-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{listing.stats.applications}</p>
                <p className="text-sm text-slate-400">Applications</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-900/50 rounded-lg">
                <CheckIcon className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{listing.stats.approvals}</p>
                <p className="text-sm text-slate-400">Approvals</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-900/50 rounded-lg">
                <ClockIcon className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{appStats.pending}</p>
                <p className="text-sm text-slate-400">Pending</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="-mb-px flex gap-6">
          {['listing', 'applications', 'upgrade'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as typeof activeTab)}
              className={`py-3 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              {tab === 'listing' && 'Listing Details'}
              {tab === 'applications' && `Applications (${appStats.pending})`}
              {tab === 'upgrade' && 'Upgrade Visibility'}
            </button>
          ))}
        </nav>
      </div>

      {/* Listing Tab */}
      {activeTab === 'listing' && (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-white">Basic Information</h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="label">Headline (max 100 characters)</label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  className="input"
                  placeholder="Join our affiliate program and earn up to 30% commission!"
                  maxLength={100}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">{formData.headline.length}/100</p>
              </div>

              <div>
                <label className="label">Description (max 2000 characters)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input min-h-[120px]"
                  placeholder="Tell affiliates about your products, target audience, and what makes your program great..."
                  maxLength={2000}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">{formData.description.length}/2000</p>
              </div>

              <div>
                <label className="label">Website URL</label>
                <input
                  type="url"
                  value={formData.websiteUrl}
                  onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                  className="input"
                  placeholder="https://yoursite.com"
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-white">Categories</h3>
              <p className="text-sm text-slate-400">
                Select up to {tiers.find(t => t.tier === (listing?.tier || 'basic'))?.maxCategories || 2} categories
              </p>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => toggleCategory(cat.slug)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      formData.categories.includes(cat.slug)
                        ? 'border-primary-500 bg-primary-900/30 text-primary-400'
                        : 'border-slate-600 bg-slate-700/50 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    <span className="text-sm font-medium">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-white">Commission Details</h3>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Commission Highlight</label>
                  <input
                    type="text"
                    value={formData.commissionHighlight}
                    onChange={(e) => setFormData({ ...formData, commissionHighlight: e.target.value })}
                    className="input"
                    placeholder="Up to 30% commission"
                    maxLength={50}
                    required
                  />
                </div>
                <div>
                  <label className="label">Cookie Duration (days)</label>
                  <input
                    type="number"
                    value={formData.cookieDuration}
                    onChange={(e) => setFormData({ ...formData, cookieDuration: Number(e.target.value) })}
                    className="input"
                    min={1}
                    max={365}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-white">Benefits for Affiliates</h3>
              <p className="text-sm text-slate-400">List up to 5 reasons to join your program</p>
            </div>
            <div className="card-body space-y-3">
              {formData.benefits.map((benefit, index) => (
                <input
                  key={index}
                  type="text"
                  value={benefit}
                  onChange={(e) => updateBenefit(index, e.target.value)}
                  className="input"
                  placeholder={`Benefit ${index + 1}`}
                  maxLength={100}
                />
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-white">Application Settings</h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="label">Application Mode</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="applicationMode"
                      value="manual_review"
                      checked={formData.applicationMode === 'manual_review'}
                      onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value as 'manual_review' })}
                      className="text-primary-500"
                    />
                    <span className="text-slate-300">Manual Review</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="applicationMode"
                      value="auto_approve"
                      checked={formData.applicationMode === 'auto_approve'}
                      onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value as 'auto_approve' })}
                      className="text-primary-500"
                    />
                    <span className="text-slate-300">Auto-Approve</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="label">Application Requirements (optional)</label>
                <textarea
                  value={formData.applicationRequirements}
                  onChange={(e) => setFormData({ ...formData, applicationRequirements: e.target.value })}
                  className="input min-h-[80px]"
                  placeholder="Describe what you're looking for in affiliates..."
                />
              </div>

              <div>
                <label className="label">Minimum Monthly Visitors (optional)</label>
                <input
                  type="number"
                  value={formData.minAudienceSize || ''}
                  onChange={(e) => setFormData({ ...formData, minAudienceSize: Number(e.target.value) || 0 })}
                  className="input"
                  placeholder="0 = No minimum"
                  min={0}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <div>
              {listing && (
                <button
                  type="button"
                  onClick={handleDeleteListing}
                  className="btn btn-secondary text-red-400 hover:text-red-300"
                >
                  Remove from Catalog
                </button>
              )}
            </div>
            <button type="submit" disabled={isSaving} className="btn btn-primary">
              {isSaving ? 'Saving...' : listing ? 'Update Listing' : 'Create Listing'}
            </button>
          </div>
        </form>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="space-y-4">
          {applications.length === 0 ? (
            <div className="card p-8 text-center">
              <UserGroupIcon className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No applications yet</h3>
              <p className="text-slate-400">
                Applications from affiliates will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.applicationId} className="card">
                  <div className="card-body">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h4 className="text-lg font-medium text-white">{app.applicantName}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            app.status === 'pending' ? 'bg-yellow-900/50 text-yellow-400' :
                            app.status === 'approved' ? 'bg-accent-900/50 text-accent-400' :
                            'bg-red-900/50 text-red-400'
                          }`}>
                            {app.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400">{app.applicantEmail}</p>
                        {app.websiteUrl && (
                          <a href={app.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-400 hover:underline">
                            {app.websiteUrl}
                          </a>
                        )}
                        {app.monthlyVisitors && (
                          <p className="text-sm text-slate-400">
                            Monthly visitors: {app.monthlyVisitors.toLocaleString()}
                          </p>
                        )}
                        <p className="text-sm text-slate-300 mt-2">{app.audienceDescription}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          Applied {new Date(app.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      {app.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReviewApplication(app.applicationId, 'approve')}
                            className="btn btn-primary btn-sm"
                          >
                            <CheckIcon className="w-4 h-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReviewApplication(app.applicationId, 'reject')}
                            className="btn btn-secondary btn-sm"
                          >
                            <XMarkIcon className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upgrade Tab */}
      {activeTab === 'upgrade' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.tier}
              className={`card ${
                tier.tier === (listing?.tier || 'basic')
                  ? 'ring-2 ring-primary-500'
                  : ''
              } ${tier.tier === 'spotlight' ? 'ring-2 ring-purple-500/50' : ''}`}
            >
              <div className="card-body space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {tier.tier === 'spotlight' && <SparklesIcon className="w-5 h-5 text-purple-400" />}
                    {tier.tier === 'featured' && <StarIcon className="w-5 h-5 text-primary-400" />}
                    {tier.tier === 'basic' && <GlobeAltIcon className="w-5 h-5 text-slate-400" />}
                    <h3 className="text-lg font-bold text-white">{tier.name}</h3>
                  </div>
                  {tier.tier === (listing?.tier || 'basic') && (
                    <span className="px-2 py-1 bg-primary-900/50 text-primary-400 text-xs rounded">
                      Current
                    </span>
                  )}
                </div>

                <div className="text-3xl font-bold text-white">
                  {tier.monthlyPrice === 0 ? (
                    'Free'
                  ) : (
                    <>
                      ${(tier.monthlyPrice / 100).toFixed(0)}
                      <span className="text-lg font-normal text-slate-400">/mo</span>
                    </>
                  )}
                </div>

                <ul className="space-y-2">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckIcon className="w-4 h-4 text-accent-400 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {tier.badges.length > 0 && (
                  <div className="flex gap-2">
                    {tier.badges.map((badge) => (
                      <span
                        key={badge}
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          badge === 'Spotlight' || badge === 'Top Pick'
                            ? 'bg-purple-900/50 text-purple-400'
                            : 'bg-primary-900/50 text-primary-400'
                        }`}
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                )}

                {tier.tier !== (listing?.tier || 'basic') && listing && (
                  <button
                    onClick={async () => {
                      try {
                        const response = await orgCatalogApi.upgradeListing(tier.tier, 'monthly');
                        if (response.data.success) {
                          toast.success(response.data.data.message);
                          loadData();
                        }
                      } catch (error: unknown) {
                        const err = error as { response?: { data?: { error?: { message?: string } } } };
                        toast.error(err.response?.data?.error?.message || 'Failed to upgrade');
                      }
                    }}
                    className={`btn w-full ${
                      tier.tier === 'spotlight' ? 'btn-primary bg-purple-600 hover:bg-purple-700' :
                      tier.tier === 'featured' ? 'btn-primary' :
                      'btn-secondary'
                    }`}
                  >
                    <ArrowUpIcon className="w-4 h-4 mr-2" />
                    {tier.monthlyPrice === 0 ? 'Downgrade' : 'Upgrade'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
